/**
 * `?agent=practice|client[&thread=…][&message=…]` on any route that offers
 * the panel opens it and hands the thread to that focus's chat once it
 * mounts (notification links; old links still say `practice_manager`,
 * `assistant` or `client_manager`, which read the same way). Which focus
 * renders is decided by the route, so the link's agent only tags the pending
 * thread. The params are consumed (removed from the URL) once applied.
 *
 * On routes without a panel (cases, the agent workspaces) this does nothing:
 * the page reads `thread`/`message` itself.
 */
import { useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { panelAgentOf, useAgentPanelStore } from "./agentPanelStore";
import { AGENT_PARAM, MESSAGE_PARAM, THREAD_PARAM } from "./routes";
import { panelAgentForPath } from "./useAgentPanelScope";

export { AGENT_PARAM, THREAD_PARAM, MESSAGE_PARAM };

export const useAgentPanelDeepLink = (): void => {
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const openWith = useAgentPanelStore((s) => s.openWith);
  const agent = panelAgentOf(params.get(AGENT_PARAM));
  const offersPanel = panelAgentForPath(pathname) !== null;
  // `params` changes on every navigation; the effect keys on the agent param
  // alone and reads the rest through these refs.
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const setParamsRef = useRef(setParams);
  setParamsRef.current = setParams;

  useEffect(() => {
    if (!agent || !offersPanel) return;
    const { current } = paramsRef;
    openWith({
      agent,
      threadId: current.get(THREAD_PARAM),
      messageId: current.get(MESSAGE_PARAM)
    });
    const next = new URLSearchParams(current);
    next.delete(AGENT_PARAM);
    next.delete(THREAD_PARAM);
    next.delete(MESSAGE_PARAM);
    setParamsRef.current(next, { replace: true });
  }, [agent, offersPanel, openWith]);
};
